// (H2) The beacon: how people use the pages, first-party, so we can see
// whether the human side works. The server puts this script on every page for
// people (http.clj with-ux); plazmi.ux checks and keeps what it sends.
//
// - A random id for this browser tab (sessionStorage), never a cookie, never
//   tied to a visitor or a Google account. The page's path as a template
//   (/@{slug}, never the slug); the referrer's domain only.
// - Events from a short list: a page view, time in view, scroll depth, clicks
//   on elements named with data-ux="…", panels opened and closed, WebGL and
//   the first 3D frame, frames a second, the camera taken in hand, a plazmi or
//   a house clicked, following, chat lines sent (a count, never the words),
//   the sign-in, claim and shop funnels, and errors (a script error's message
//   and file name only).
// - Sent with sendBeacon in small batches, about every ten seconds and as the
//   page goes. Nothing at all when the browser asks not to be tracked (Global
//   Privacy Control or Do Not Track).
import type { UxFields } from './uxq.js';

type Ev = UxFields & { n: string; ref?: string; vp?: string; dev?: string; msg?: string; src?: string };

const W = window as any;
const N = navigator as any;

const optedOut = () => {
  try {
    return N.globalPrivacyControl === true || N.doNotTrack === '1' || N.doNotTrack === 'yes' || W.doNotTrack === '1' || N.msDoNotTrack === '1';
  } catch { return true; }
};

// ----- the page's template: never a name, a code or a token
const STATIC = new Set(['/', '/follow', '/now', '/homestead', '/commons', '/hall', '/landing', '/gazette', '/noticeboard', '/dances',
  '/archive', '/claim', '/shop', '/auth/google/callback', '/left-lit', '/terms', '/privacy', '/connect', '/post-office', '/print-shop',
  '/market', '/workshop', '/reading-room', '/cafe', '/town-hall', '/pu', '/dev/checkout', '/wander']);
const SHAPES: [RegExp, string | ((m: RegExpExecArray) => string)][] = [
  [/^\/@[^/]+$/, '/@{slug}'],
  [/^\/@[^/]+\/(dance|made|today|prime)$/, (m) => `/@{slug}/${m[1]}`],
  [/^\/shop\/@[^/]+$/, '/shop/@{slug}'],
  [/^\/dance\/[^/]+$/, '/dance/{name}'],
  [/^\/gazette\/[^/]+\/arrivals$/, '/gazette/{day}/arrivals'],
  [/^\/gazette\/[^/]+$/, '/gazette/{day}'],
  [/^\/archive\/day\/[^/]+$/, '/archive/day/{n}'],
  [/^\/plot\/[^/]+$/, '/plot/{n}'],
  [/^\/ask\/[^/]+$/, '/ask/{code}'],
  [/^\/join\/[^/]+$/, '/join/{token}'],
  [/^\/home\/[^/]+\/key$/, '/home/{slug}/key'],
  [/^\/oauth\/consent\/[^/]+$/, '/oauth/consent/{id}'],
];
function template(path: string): string {
  const p = path.replace(/\/+$/, '') || '/';
  if (STATIC.has(p)) return p;
  for (const [re, t] of SHAPES) { const m = re.exec(p); if (m) return typeof t === 'string' ? t : t(m); }
  return '/other';
}

function rand() {
  const b = new Uint8Array(12);
  crypto.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}
let memId = '';
function tabId() {
  try {
    let s = sessionStorage.getItem('pz.ux.s');
    if (!s || !/^[a-z0-9]{8,32}$/.test(s)) { s = rand(); sessionStorage.setItem('pz.ux.s', s); }
    return s;
  } catch { return (memId ||= rand()); }
}
const ss = {
  get(k: string) { try { return sessionStorage.getItem(k); } catch { return null; } },
  set(k: string, v: string) { try { sessionStorage.setItem(k, v); } catch { /* fine */ } },
  del(k: string) { try { sessionStorage.removeItem(k); } catch { /* fine */ } },
};

function refDomain(): string | undefined {
  try {
    if (!document.referrer) return 'direct';
    const u = new URL(document.referrer);
    if (u.host === location.host) return undefined; // from our own pages: the path between pages says it
    return u.hostname.replace(/^www\./, '').toLowerCase().slice(0, 64);
  } catch { return undefined; }
}
const vpBucket = () => { const w = innerWidth; return w < 480 ? 'xs' : w < 768 ? 's' : w < 1024 ? 'm' : w < 1440 ? 'l' : 'xl'; };
function device() {
  const coarse = matchMedia('(pointer: coarse)').matches;
  const short = Math.min(screen.width || innerWidth, screen.height || innerHeight);
  return coarse ? (short < 600 ? 'phone' : 'tablet') : 'desktop';
}

function start() {
  if (W.__pzuxOn) return;
  W.__pzuxOn = true;
  if (optedOut() || !navigator.sendBeacon) {
    // the queue's notes go nowhere
    W.__pzux = { push() { return 0; } };
    W.__pzuxh = { push() { return 0; } };
    return;
  }
  const page = template(location.pathname);
  const id = tabId();
  const q: Ev[] = [];
  const add = (e: Ev) => { if (q.length < 240) q.push(e); };
  let errs = 0;

  // ----- frames a second, on a 3D page: its own small count, a few seconds at a time
  let fpsOn = false, frames = 0, t0 = 0;
  const secs: Record<string, number> = {};
  function fps() {
    if (fpsOn) return;
    fpsOn = true;
    t0 = performance.now();
    const tick = () => { frames++; requestAnimationFrame(tick); };
    requestAnimationFrame(tick);
    setInterval(() => {
      const now = performance.now(), dt = (now - t0) / 1000;
      if (document.visibilityState === 'visible' && dt > 1 && dt < 30) {
        const f = frames / dt;
        const b = f < 15 ? 'lt15' : f < 30 ? '15-30' : f < 45 ? '30-45' : f < 55 ? '45-55' : '55+';
        secs[b] = (secs[b] || 0) + dt;
      }
      frames = 0; t0 = now;
    }, 5000);
    document.addEventListener('visibilitychange', () => { frames = 0; t0 = performance.now(); });
  }
  function fpsOut() {
    for (const b of Object.keys(secs)) { const v = Math.round(secs[b]); if (v >= 1) { add({ n: 'fps', k: b, v }); secs[b] -= v; } }
  }

  // ----- what the bundles noted, and will note
  const throttled = new Map<string, number>();
  const take = (x: any) => {
    if (!Array.isArray(x) || typeof x[0] !== 'string') return 0;
    const n = x[0] as string, f = (x[1] || {}) as UxFields;
    if (n === 'cam') {
      // the camera taken in hand: once in a few seconds is plenty
      const key = `cam:${f.k}`, t = performance.now();
      if (t - (throttled.get(key) ?? -1e9) < 4000) return 0;
      throttled.set(key, t);
    }
    if (n === 'gl' && f.k === 'ok') fps();
    if (n === 'error' && errs++ >= 8) return 0;
    add({ n, ...f });
    return 0;
  };
  const was = Array.isArray(W.__pzux) ? W.__pzux : [];
  W.__pzux = { push: take };
  for (const x of was) take(x);
  const hides: (() => void)[] = Array.isArray(W.__pzuxh) ? W.__pzuxh : [];
  W.__pzuxh = { push(fn: () => void) { hides.push(fn); return 0; } };

  // ----- sending
  const send = () => {
    while (q.length) {
      const body = JSON.stringify({ s: id, p: page, e: q.splice(0, 50) });
      try { navigator.sendBeacon('/api/ux', body); } catch { return; }
    }
  };
  const flush = () => { fpsOut(); send(); };
  setInterval(flush, 10000);

  // ----- the view, and time in view
  const u = new URLSearchParams(location.search);
  const view = () => add({ n: 'view', ref: refDomain(), vp: vpBucket(), dev: device(), ...(u.has('follow') ? { k: 'follow' } : {}) });
  view();
  let shown = 0, since = document.visibilityState === 'visible' ? performance.now() : -1;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') since = performance.now();
    else { if (since >= 0) shown += performance.now() - since; since = -1; send(); }
  });
  addEventListener('pagehide', () => {
    for (const fn of hides) { try { fn(); } catch { /* fine */ } }
    if (since >= 0) { shown += performance.now() - since; since = performance.now(); }
    add({ n: 'leave', v: Math.round(shown) });
    shown = 0;
    flush();
  });
  // back to it from the back button's cache: a view again
  addEventListener('pageshow', (e) => { if ((e as PageTransitionEvent).persisted) { view(); since = performance.now(); } });

  // ----- how far down
  const deep = new Set<number>();
  addEventListener('scroll', () => {
    const h = document.documentElement.scrollHeight - innerHeight;
    if (h < 40) return;
    const at = Math.min(100, ((scrollY || 0) / h) * 100);
    for (const d of [25, 50, 75, 100]) if (at >= d - 1 && !deep.has(d)) { deep.add(d); add({ n: 'scroll', v: d }); }
  }, { passive: true });

  // ----- clicks on named things, the funnels' steps, panels
  const funnel = (spec: string | null) => {
    const [el, k] = (spec || '').split(':');
    if (el && k) add({ n: 'funnel', el, k });
  };
  document.addEventListener('click', (e) => {
    const t = e.target as Element | null;
    if (!t || !t.closest) return;
    const g = t.closest('a[href^="/auth/google"]');
    if (g) { funnel('signin:start'); ss.set('pz.ux.si', String(Date.now())); }
    const named = t.closest('[data-ux]');
    // a <details>' own summary is a panel, below
    if (named && named.tagName !== 'DETAILS') add({ n: 'click', el: (named.getAttribute('data-ux') || '').slice(0, 40) });
    else if (g) add({ n: 'click', el: 'sign-in-google' });
    const f = t.closest('[data-ux-funnel]');
    if (f) funnel(f.getAttribute('data-ux-funnel'));
  }, true);
  document.addEventListener('toggle', (e) => {
    const d = e.target as HTMLDetailsElement;
    if (d && d.tagName === 'DETAILS' && d.dataset.ux) add({ n: 'panel', el: d.dataset.ux, k: d.open ? 'open' : 'close' });
  }, true);
  document.addEventListener('submit', (e) => {
    const f = (e.target as Element)?.closest?.('form[data-ux-submit]') as HTMLFormElement | null;
    if (!f) return;
    const code = f.querySelector('input[name=code]') as HTMLInputElement | null;
    if (!code || code.value.trim()) funnel(f.getAttribute('data-ux-submit'));
  }, true);

  // ----- what the page says it shows (server-set pages): a funnel's step
  const marks = () => {
    const seen = new Set<string>();
    document.querySelectorAll('[data-ux-mark]').forEach((m) => {
      const s = m.getAttribute('data-ux-mark') || '';
      if (seen.has(s)) return;
      seen.add(s);
      if (s === 'claim:waiting') { ss.set('pz.ux.cw', '1'); funnel(s); }
      // the agent confirmed its human, in a tab that was waiting for it
      else if (s === 'claim:mine') { if (ss.get('pz.ux.cw')) { ss.del('pz.ux.cw'); funnel('claim:confirmed'); } }
      else funnel(s);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', marks); else marks();

  // ----- back from Google: signed in?
  const si = Number(ss.get('pz.ux.si') || 0);
  if (si) {
    ss.del('pz.ux.si');
    if (Date.now() - si < 30 * 60000) {
      funnel('signin:google');
      fetch('/api/visitors/me', { credentials: 'same-origin', headers: { accept: 'application/json' } })
        .then((r) => r.json()).then((j) => { if (j && j.id) funnel('signin:signed-in'); }).catch(() => {});
    }
  }

  // ----- errors: the message and the script's file, nothing more (plazmi.ux tidies both again)
  const jsError = (msg: string, src: string) => { if (errs++ < 8) add({ n: 'error', k: 'js', msg: msg.slice(0, 300), src: src.slice(0, 200) }); };
  addEventListener('error', (e) => { if (e instanceof ErrorEvent) jsError(String(e.message || ''), String(e.filename || '')); });
  addEventListener('unhandledrejection', (e) => {
    const r: any = (e as PromiseRejectionEvent).reason;
    jsError(String((r && r.message) || r || ''), String((r && r.fileName) || 'promise'));
  });
}

try { start(); } catch { /* counting never gets in the way */ }
